import { memo, useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const COLORS = {
  number: "#1B6E3C", // deep green — Number series
  area: "#8FCB3E", // lime — Area series
  areaText: "#6FA32B", // darker lime for text (axis ticks / bar labels)
  amount: "#3457D5", // blue — optional Amount (₹) series (CVD-validated against number/area)
  ink: "#1C2A22",
  sub: "#6B7A70",
  grid: "#E8EEE9",
  focusRing: "#134D2A",
};

const DIM_OPACITY = 0.25;
const ZERO_OPACITY = 0.25;

type SeriesKey = "number" | "area" | "amount";

type ChartRow = {
  status: string;
  number: number;
  area: number;
  // Optional 3rd series (₹). Absent entirely for callers that don't pass it
  // (e.g. Market Watch), so its axis/legend/tooltip row never render there.
  amount?: number;
  // Land-parcel count behind the baseline row, shown in the summary line in
  // place of the ₹ amount when supplied. Does not add a chart series — bars,
  // axis and legend are unaffected; it only changes what the "Created: …"
  // sentence reports.
  parcels?: number;
};

type StatusBarChartProps = {
  data: { name: string; uv: number; pv: number; amount?: number; parcels?: number }[];
  label: string;
  /** Name of the optional 3rd (₹) series — legend chip, tooltip row and axis.
   *  Callers whose amount is not simply "the money at this stage" should say
   *  what it is (the Fund Dashboard passes "Requested (₹)", since its bars carry
   *  total_cost while a separate KPI reports released). Defaults to the generic
   *  label so existing callers (Market Watch) are unaffected. */
  amountLabel?: string;
};

const fmt = (value: number) => (Number(value) || 0).toLocaleString("en-IN");

// Bar-top labels have ~20px of bar width to sit above — a full digit-grouped
// rupee value ("₹8,10,84,968.32") is 2-3x wider than that and collides with
// the neighbouring bars' labels. Round to whole numbers / compact to Cr·L·K
// for the LABEL only; tooltips still show full precision via `fmt`.
const fmtWhole = (value: number) =>
  Math.round(Number(value) || 0).toLocaleString("en-IN");

const compactAmount = (value: number) => {
  const v = Number(value) || 0;
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(2)}Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(2)}L`;
  if (a >= 1e3) return `${sign}₹${(a / 1e3).toFixed(1)}K`;
  return `${sign}₹${fmtWhole(a)}`;
};

const sharePct = (value: number, base?: number) =>
  base ? `${Math.round((value / base) * 100)}%` : "";

const SERIES: {
  key: SeriesKey;
  label: string;
  yAxisId: string;
  color: string;
  labelColor: string;
  showZeroLabel: boolean;
  /** Bar-top label formatter — distinct from the (full-precision) tooltip. */
  formatLabel: (value: number) => string;
}[] = [
  {
    key: "number",
    label: "Number",
    yAxisId: "num",
    color: COLORS.number,
    labelColor: COLORS.number,
    showZeroLabel: true,
    formatLabel: fmt,
  },
  {
    key: "area",
    label: "Area",
    yAxisId: "area",
    color: COLORS.area,
    labelColor: COLORS.areaText,
    showZeroLabel: false, // avoid a doubled "0 0" on empty statuses
    formatLabel: fmt, // overridden to fmtWhole only when the 3rd series is on
  },
  {
    key: "amount",
    label: "Amount (₹)",
    yAxisId: "amount",
    color: COLORS.amount,
    labelColor: COLORS.amount,
    showZeroLabel: false,
    formatLabel: compactAmount,
  },
];

/* Wrapping tick for long status names */
const WrapTick = ({ x, y, payload }: any) => {
  const words = String(payload.value).split(" ");
  const lines: string[] = [];
  let line = "";
  words.forEach((word) => {
    if ((line + " " + word).trim().length > 10) {
      lines.push(line.trim());
      line = word;
    } else {
      line = (line + " " + word).trim();
    }
  });
  if (line) lines.push(line);

  return (
    <g transform={`translate(${x},${y + 8})`}>
      {lines.map((text, index) => (
        <text
          key={index}
          y={index * 13}
          textAnchor="middle"
          fontSize={11}
          fill={COLORS.ink}
          fontWeight={500}
        >
          {text}
        </text>
      ))}
    </g>
  );
};

const TooltipRow = ({
  swatch,
  name,
  value,
  pct,
}: {
  swatch: string;
  name: string;
  value: string;
  pct: string;
}) => (
  <Box
    sx={{ display: "flex", alignItems: "center", gap: "8px", lineHeight: "20px" }}
  >
    <Box
      component="span"
      sx={{
        width: "9px",
        height: "9px",
        borderRadius: "3px",
        backgroundColor: swatch,
        flexShrink: 0,
      }}
    />
    <Box component="span" sx={{ color: "#DCE7E0" }}>
      {name}
    </Box>
    <Box component="span" sx={{ marginLeft: "auto", fontWeight: 700 }}>
      {value}
    </Box>
    <Box
      component="span"
      sx={{ color: "#8FA89A", width: "40px", textAlign: "right" }}
    >
      {pct}
    </Box>
  </Box>
);

const ChartTooltip = ({
  active,
  payload,
  label,
  baseline,
  visible,
  amountLabel,
}: any) => {
  if (!active || !payload?.length) return null;
  const row: ChartRow = payload[0]?.payload;

  return (
    <Box
      sx={{
        backgroundColor: "#122B1C",
        color: "#fff",
        borderRadius: "12px",
        padding: "10px 14px",
        fontSize: "12px",
        minWidth: "200px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
      }}
    >
      <Box sx={{ color: "#A8D5BA", marginBottom: "6px", fontWeight: 600 }}>
        {label}
      </Box>
      {visible.number && (
        <TooltipRow
          swatch={COLORS.number}
          name="Number"
          value={fmt(row.number)}
          pct={sharePct(row.number, baseline?.number)}
        />
      )}
      {visible.area && (
        <TooltipRow
          swatch={COLORS.area}
          name="Area (ha)"
          value={fmt(row.area)}
          pct={sharePct(row.area, baseline?.area)}
        />
      )}
      {visible.amount && row.amount !== undefined && (
        <TooltipRow
          swatch={COLORS.amount}
          name={amountLabel ?? "Amount (₹)"}
          value={compactAmount(row.amount)}
          pct={sharePct(row.amount, baseline?.amount)}
        />
      )}
    </Box>
  );
};

const StatusBarChart = ({
  data,
  label,
  amountLabel = "Amount (₹)",
}: StatusBarChartProps) => {
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    number: true,
    area: true,
    amount: true,
  });
  const [focused, setFocused] = useState<string | null>(null);

  const rows = useMemo<ChartRow[]>(
    () =>
      (data ?? []).map((item) => ({
        status: item.name,
        number: Number(item.uv) || 0,
        area: Number(item.pv) || 0,
        amount: item.amount !== undefined ? Number(item.amount) || 0 : undefined,
        parcels: item.parcels !== undefined ? Number(item.parcels) || 0 : undefined,
      })),
    [data],
  );

  // first row is the "All Selected" bucket — the % base in the tooltip
  const baseline = rows[0];

  // Amount is opt-in: only present (and only rendered — axis, legend chip,
  // bar, tooltip row) when every caller-supplied row actually has it.
  const hasAmount = useMemo(
    () => rows.length > 0 && rows.every((row) => row.amount !== undefined),
    [rows],
  );

  const activeSeries = useMemo(() => {
    const base = SERIES.filter(
      (series) => series.key !== "amount" || hasAmount,
    );
    if (!hasAmount) return base; // 2-series case (e.g. Market Watch) — untouched
    // 3 bars per category need tighter labels — shorten Area's bar-top label
    // to whole hectares (the tooltip still shows full 2-decimal precision).
    return base.map((series) => {
      if (series.key === "area") return { ...series, formatLabel: fmtWhole };
      if (series.key === "amount") return { ...series, label: amountLabel };
      return series;
    });
  }, [hasAmount, amountLabel]);

  // 3 grouped bars per category need a bit less width each than 2 did.
  const maxBarSize = hasAmount ? 22 : 34;

  const zeroRows = useMemo(
    () => rows.filter((row) => !row.number && !row.area && !row.amount),
    [rows],
  );

  const toggleSeries = useCallback((key: SeriesKey) => {
    setVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // never allow every series off
      const anyOn = activeSeries.some((series) => next[series.key]);
      return anyOn ? next : prev;
    });
  }, [activeSeries]);

  const handleChartClick = useCallback((event: any) => {
    const status = event?.activeLabel;
    if (status) setFocused((prev) => (prev === status ? null : status));
  }, []);

  if (!rows.length) return null;

  const opacityFor = (row: ChartRow, value: number | undefined) => {
    if (!value) return ZERO_OPACITY;
    return focused === null || focused === row.status ? 1 : DIM_OPACITY;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${COLORS.grid}`,
        borderRadius: "16px",
        padding: "20px",
        color: COLORS.ink,
      }}
    >
      {/* header: title + interactive legend chips */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "4px",
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: "16px", fontWeight: 700, color: COLORS.ink }}
          >
            {label}
          </Typography>
          {baseline && (
            <Typography
              sx={{ fontSize: "12px", color: COLORS.sub, marginTop: "2px" }}
            >
              {baseline.status}: {fmt(baseline.number)} · {fmt(baseline.area)}{" "}
              ha
              {/* Land-parcel count takes the ₹ amount's place here when a
                  caller supplies it (the Fund Dashboard does) — the amount
                  still drives the chart's bars/axis/legend, this sentence
                  just reports parcels instead of money. Callers without
                  `parcels` (e.g. Market Watch) keep the amount as before. */}
              {baseline.parcels !== undefined
                ? ` · ${fmt(baseline.parcels)} land parcel${
                    baseline.parcels === 1 ? "" : "s"
                  }`
                : baseline.amount !== undefined &&
                  ` · ${compactAmount(baseline.amount)}`}
              {" — click a bar to focus, legend to toggle"}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: "8px" }}>
          {activeSeries.map((series) => {
            const isOn = visible[series.key];
            return (
              <Box
                key={series.key}
                component="button"
                type="button"
                onClick={() => toggleSeries(series.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "5px 12px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  border: `1.5px solid ${isOn ? series.color : COLORS.grid}`,
                  backgroundColor: isOn ? `${series.color}18` : "transparent",
                  color: isOn ? series.labelColor : COLORS.sub,
                  textDecoration: isOn ? "none" : "line-through",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "3px",
                    backgroundColor: isOn ? series.color : COLORS.grid,
                  }}
                />
                {series.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ width: "100%", height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 28, right: 8, left: 0, bottom: 8 }}
            barGap={hasAmount ? 3 : 4}
            barCategoryGap={hasAmount ? "20%" : "28%"}
            onClick={handleChartClick}
          >
            <CartesianGrid vertical={false} stroke={COLORS.grid} />
            <XAxis
              dataKey="status"
              tick={<WrapTick />}
              tickLine={false}
              axisLine={{ stroke: COLORS.grid }}
              interval={0}
              height={52}
            />
            <YAxis
              yAxisId="num"
              tick={{ fontSize: 11, fill: COLORS.number }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Number",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11, fill: COLORS.number, fontWeight: 600 },
              }}
            />
            <YAxis
              yAxisId="area"
              orientation="right"
              tick={{ fontSize: 11, fill: COLORS.areaText }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Area (ha)",
                angle: 90,
                position: "insideRight",
                style: { fontSize: 11, fill: COLORS.areaText, fontWeight: 600 },
              }}
            />
            {hasAmount && (
              // No text label here (would collide with "Area (ha)" above) —
              // the blue tick color + legend chip + tooltip carry its identity.
              <YAxis
                yAxisId="amount"
                orientation="right"
                tick={{ fontSize: 11, fill: COLORS.amount }}
                tickLine={false}
                axisLine={false}
              />
            )}
            <Tooltip
              content={
                <ChartTooltip
                  baseline={baseline}
                  visible={visible}
                  amountLabel={amountLabel}
                />
              }
              cursor={{ fill: "rgba(27, 110, 60, 0.06)" }} // soft tint, not a gray slab
            />
            <ReferenceLine yAxisId="num" y={0} stroke={COLORS.grid} />

            {activeSeries.filter((series) => visible[series.key]).map((series) => (
              <Bar
                key={series.key}
                yAxisId={series.yAxisId}
                dataKey={series.key}
                name={series.label}
                radius={[6, 6, 0, 0]}
                maxBarSize={maxBarSize}
                // keep zero-value statuses visible as a 2px stub
                minPointSize={(value: number) => (value === 0 ? 2 : 0)}
              >
                {rows.map((row) => (
                  <Cell
                    key={row.status}
                    fill={series.color}
                    fillOpacity={opacityFor(row, row[series.key])}
                    stroke={focused === row.status ? COLORS.focusRing : "none"}
                    strokeWidth={focused === row.status ? 2 : 0}
                    cursor="pointer"
                  />
                ))}
                <LabelList
                  dataKey={series.key}
                  position="top"
                  formatter={(value: any) =>
                    value === 0
                      ? series.showZeroLabel
                        ? "0"
                        : ""
                      : series.formatLabel(value)
                  }
                  style={{
                    fontSize: hasAmount ? 9 : 10,
                    fontWeight: 700,
                    fill: series.labelColor,
                  }}
                />
              </Bar>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* empty-status strip: makes the zeros informative instead of invisible */}
      {zeroRows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "6px",
            marginTop: "8px",
            paddingTop: "10px",
            borderTop: `1px dashed ${COLORS.grid}`,
            fontSize: "11px",
            color: COLORS.sub,
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            No records in:
          </Box>
          {zeroRows.map((row) => (
            <Box
              key={row.status}
              component="span"
              sx={{
                padding: "2px 8px",
                borderRadius: "999px",
                backgroundColor: "#F1F5F1",
                border: `1px solid ${COLORS.grid}`,
              }}
            >
              {row.status}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default memo(StatusBarChart);
